import React, { useState } from 'react';

const PLATFORM_OPTIONS = [
  { id: 'chatgpt', label: 'ChatGPT' },
  { id: 'claude', label: 'Claude' },
  { id: 'both', label: 'Both' },
];

const ROLE_OPTIONS = [
  { id: 'all', label: 'All' },
  { id: 'dev', label: 'Dev' },
  { id: 'sales-marketing', label: 'Sales & Marketing' },
  { id: 'copy', label: 'Copy' },
  { id: 'executive', label: 'Executive' },
];

const PROFILE_GUIDES = {
  dev: {
    chatgpt: {
      summary: 'Use ChatGPT for shared knowledge, internal docs, and cross-team research. Keep local repo work and local MCP execution out of the web surface.',
      focus: ['Prompt and skill libraries', 'Remote OAuth connectors', 'Read-only business context', 'Approval-heavy workflows'],
      emphasis: 'Push broad knowledge access into ChatGPT. Keep implementation and local toolchains in Claude Code or Codex.',
    },
    claude: {
      summary: 'Use Claude for engineering-heavy MCP work: local stdio servers, repo access, CLI auth, and tool approvals.',
      focus: ['Claude Code MCP setup', 'Salesforce DX / GitHub / local servers', 'Design-to-code and repo workflows', 'Write-scoped approvals'],
      emphasis: 'Optimize for local execution, explicit tool scopes, and operator-only write paths.',
    },
    both: {
      summary: 'Split by job: ChatGPT for workspace knowledge and discovery, Claude for execution and local engineering tooling.',
      focus: ['ChatGPT for doc retrieval and shared research', 'Claude for repo, local MCP, and implementation', 'Shared approval policies', 'Clear handoff between discovery and execution'],
      emphasis: 'Do not force one surface to do both jobs badly.',
    },
    sections: ['platforms', 'surface', 'mcp', 'agents', 'shared-skills', 'skills', 'playbooks', 'replacement', 'deployment'],
  },
  'sales-marketing': {
    chatgpt: {
      summary: 'ChatGPT is the cleaner front door for broad non-technical rollout. Prioritize remote OAuth connectors and simple approvals.',
      focus: ['Slack, Dropbox, Salesforce-adjacent context', 'Meeting prep and account research', 'Knowledge packs for campaigns and pitch work', 'Low-friction remote connectors'],
      emphasis: 'Prefer connectors that can be rolled out centrally without local setup.',
    },
    claude: {
      summary: 'Claude is workable for smaller operator groups, especially when desktop connectors or light technical setup are acceptable.',
      focus: ['Connector-based research', 'Campaign support workflows', 'Light orchestration', 'Smaller pilot teams'],
      emphasis: 'Use Claude where the team can tolerate some connector setup discipline.',
    },
    both: {
      summary: 'Use ChatGPT for broad commercial access and Claude for specialist operator workflows that need a tighter connector loop.',
      focus: ['ChatGPT for sales research and shared knowledge', 'Claude for specialist campaign and ops helpers', 'Consistent approval language', 'Connector overlap kept intentional'],
      emphasis: 'Separate broad rollout from specialist execution.',
    },
    sections: ['platforms', 'surface', 'mcp', 'shared-skills', 'playbooks', 'replacement', 'deployment'],
  },
  copy: {
    chatgpt: {
      summary: 'ChatGPT works best as the copy knowledge hub: prompt packs, brand rules, output contracts, and grounded research.',
      focus: ['Prompt and style libraries', 'Brand and campaign rules', 'Knowledge retrieval from Dropbox and docs', 'Draft-first creative workflows'],
      emphasis: 'Keep the copy team close to curated markdown, not raw tool complexity.',
    },
    claude: {
      summary: 'Claude is better when copy workflows need tighter connector context, desktop knowledge areas, or specialist local helper stacks.',
      focus: ['Claude project knowledge', 'Connector-backed drafting', 'Prompt iteration with external context', 'Specialist creative operators'],
      emphasis: 'Use Claude where the copy workflow needs more control than web-only ChatGPT gives.',
    },
    both: {
      summary: 'Use ChatGPT for shared prompt libraries and brand-safe drafting, then Claude for specialist connector-backed refinement where needed.',
      focus: ['Shared prompt library in ChatGPT', 'Specialist refinement in Claude', 'Cross-surface output contracts', 'Minimal context duplication'],
      emphasis: 'Standardize the rules once, then execute on the surface that fits the operator.',
    },
    sections: ['platforms', 'surface', 'mcp', 'shared-skills', 'skills', 'playbooks', 'deployment'],
  },
  executive: {
    chatgpt: {
      summary: 'ChatGPT is the strongest executive-facing surface for broad summaries, company knowledge, and low-friction connector access.',
      focus: ['Executive briefs', 'Cross-functional summaries', 'Revenue and risk reporting', 'Read-heavy business connectors'],
      emphasis: 'Optimize for clarity, coverage, and low operator friction.',
    },
    claude: {
      summary: 'Claude is better suited to analyst and operator workflows that support executive reporting rather than direct executive rollout.',
      focus: ['Analyst-side orchestration', 'Connector-backed brief assembly', 'Operator workflows behind the brief', 'Controlled MCP access'],
      emphasis: 'Treat Claude as the back-office operator surface behind executive outputs.',
    },
    both: {
      summary: 'Use ChatGPT as the executive front door and Claude as the operator layer assembling the heavier connector-backed workflows behind it.',
      focus: ['ChatGPT for leadership consumption', 'Claude for operational assembly', 'Escalation and approval design', 'Thin orchestrator patterns'],
      emphasis: 'Present one clean executive surface, keep execution complexity behind it.',
    },
    sections: ['platforms', 'surface', 'mcp', 'agents', 'shared-skills', 'replacement', 'deployment'],
  },
};

function withMeta(item, meta = {}) {
  return { ...item, ...meta };
}

function matchesPlatform(item, platform) {
  if (!item.platforms || item.platforms.length === 0 || item.platforms.includes('both')) return true;
  if (platform === 'both') return item.platforms.includes('chatgpt') || item.platforms.includes('claude');
  return item.platforms.includes(platform);
}

function matchesRole(item, role) {
  if (role === 'all') return true;
  return !item.roles || item.roles.length === 0 || item.roles.includes(role);
}

function filterItems(items, platform, role) {
  if (!items) return items;
  return items.filter(item => matchesPlatform(item, platform) && matchesRole(item, role));
}

const ExternalIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 4, opacity: 0.5 }}>
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 0 2-2h6"/>
    <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
  </svg>
);

const ChevronDown = ({ open }) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
    style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s', flexShrink: 0 }}>
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);

/* ── Surface comparison ───────────────────────────────────────── */
const SURFACES = [
  withMeta({
    name: 'ChatGPT Web',
    bestUse: 'End-user workspace rollout, deep research, company knowledge',
    howAdded: 'Settings → Apps, custom MCP app via developer mode',
    writes: 'Business / Enterprise / Edu beta with approval controls',
    constraints: 'Remote MCP only; web only; no mobile or voice',
  }, { platforms: ['chatgpt'], roles: ['dev', 'sales-marketing', 'copy', 'executive'] }),
  withMeta({
    name: 'Codex CLI / IDE',
    bestUse: 'Developer agents, repo work, plugin bundles, sub-agents',
    howAdded: 'codex mcp add, config.toml, plugin install',
    writes: 'Controlled per server/tool approval mode',
    constraints: 'No ChatGPT workspace rollout; technical users only',
  }, { platforms: ['chatgpt'], roles: ['dev'] }),
  withMeta({
    name: 'Claude.ai Web / Desktop',
    bestUse: 'Connector-based knowledge work, lightweight orchestration',
    howAdded: 'Settings → Connectors → Add custom connector',
    writes: 'Connector-dependent; OAuth through Claude.ai',
    constraints: 'Some hosted connectors require Claude.ai login',
  }, { platforms: ['claude'], roles: ['dev', 'sales-marketing', 'copy', 'executive'] }),
  withMeta({
    name: 'Claude Code',
    bestUse: 'Engineering, design-to-code, local tooling, mixed MCP stacks',
    howAdded: 'claude mcp add, .mcp.json, plugins',
    writes: 'Yes, with /mcp auth and client approvals',
    constraints: 'Best for technical users; project/user scope required',
  }, { platforms: ['claude'], roles: ['dev'] }),
];

/* ── AI Platforms ─────────────────────────────────────────────── */
const PLATFORMS = [
  withMeta({
    title: 'Claude Code Overview',
    desc: 'Terminal, desktop, browser, and IDE surfaces. MCP servers added in Claude.ai automatically appear in Claude Code when logged in.',
    url: 'https://docs.anthropic.com/en/docs/claude-code/overview',
    tags: ['Claude', 'CLI'],
  }, { platforms: ['claude'], roles: ['dev', 'copy'] }),
  withMeta({
    title: 'Claude Code MCP Guide',
    desc: 'HTTP transport setup, STDIO servers, .mcp.json config, security and prompt-injection warnings.',
    url: 'https://docs.anthropic.com/en/docs/claude-code/mcp',
    tags: ['Claude', 'MCP'],
  }, { platforms: ['claude'], roles: ['dev'] }),
  withMeta({
    title: 'Model Context Protocol Spec',
    desc: 'Official MCP specification — transports, tool definitions, auth patterns, security guidance.',
    url: 'https://modelcontextprotocol.io',
    tags: ['MCP', 'Spec'],
  }, { platforms: ['both'], roles: ['dev', 'sales-marketing', 'copy', 'executive'] }),
  withMeta({
    title: 'OpenAI MCP (Responses API)',
    desc: 'MCP tooling in the Responses API. Defaults to approval requests before sharing data with remote servers.',
    url: 'https://platform.openai.com/docs/mcp',
    tags: ['OpenAI', 'MCP'],
  }, { platforms: ['chatgpt'], roles: ['dev'] }),
  withMeta({
    title: 'ChatGPT Connectors / Apps',
    desc: 'Apps & Connectors in ChatGPT Web. Custom MCP apps require developer mode. Business/Enterprise/Edu logs all app calls.',
    url: 'https://help.openai.com/en/articles/11487775-connectors-in-chatgpt',
    tags: ['OpenAI', 'ChatGPT'],
  }, { platforms: ['chatgpt'], roles: ['dev', 'sales-marketing', 'copy', 'executive'] }),
  withMeta({
    title: 'Codex CLI MCP',
    desc: 'Codex CLI MCP support — local STDIO, remote streamable HTTP, bearer auth, OAuth, and plugin bundles.',
    url: 'https://developers.openai.com/codex/mcp',
    tags: ['OpenAI', 'CLI'],
  }, { platforms: ['chatgpt'], roles: ['dev'] }),
];

/* ── MCP Integrations ─────────────────────────────────────────── */
const MCP_INTEGRATIONS = [
  withMeta({
    title: 'Dropbox MCP',
    desc: 'Official remote MCP server. Documented setup for Claude Code, Claude Web, ChatGPT Web, and Codex. Fastest remote-OAuth win.',
    url: 'https://help.dropbox.com/integrations/connect-dropbox-mcp-server',
    tags: ['Remote', 'OAuth'],
    context: 'W / A / C', ease: 'Low', priority: 'High',
    note: 'claude mcp add --transport http DropboxMCP https://mcp.dropbox.com/mcp',
  }, { platforms: ['both'], roles: ['sales-marketing', 'copy', 'executive', 'dev'] }),
  withMeta({
    title: 'GitHub MCP',
    desc: 'Remote PAT/OAuth or local Docker/binary. Force read-only mode, role-specific toolsets. Top-tier integration.',
    url: 'https://github.com/github/github-mcp-server',
    tags: ['Remote', 'Local'],
    context: 'A / C', ease: 'Medium', priority: 'High',
  }, { platforms: ['both'], roles: ['dev'] }),
  withMeta({
    title: 'YouTrack MCP',
    desc: 'Built-in /mcp endpoint. HTTP remote + local mcp-remote proxy. Issues, articles, time tracking. Great for agentic triage.',
    url: 'https://www.jetbrains.com/help/youtrack/server/model-context-protocol-server.html',
    tags: ['Remote', 'Bearer'],
    context: 'W / C', ease: 'Medium', priority: 'High',
  }, { platforms: ['both'], roles: ['dev', 'executive'] }),
  withMeta({
    title: 'Slack MCP',
    desc: 'Partner-supported. Search messages/files/channels, manage canvases, draft posts. Wrap with approval policy.',
    url: 'https://slack.com/help/articles/48855576908307-Guide-to-the-Slack-MCP-server',
    tags: ['Remote', 'OAuth'],
    context: 'W / A', ease: 'Medium', priority: 'High',
  }, { platforms: ['both'], roles: ['sales-marketing', 'executive', 'copy'] }),
  withMeta({
    title: 'Salesforce DX MCP',
    desc: 'Beta/pilot. Exposes org, data, metadata, and user toolsets. Read-first; stage write actions with explicit approval flows.',
    url: 'https://github.com/salesforcecli/mcp',
    tags: ['Local', 'Beta'],
    context: 'C', ease: 'High', priority: 'High',
  }, { platforms: ['claude'], roles: ['dev'] }),
  withMeta({
    title: 'Shopify MCP',
    desc: 'Two products: Storefront MCP (buyer-agent) and AI Toolkit/Dev MCP (operators). Implement as split architecture.',
    url: 'https://shopify.dev/docs/apps/build/storefront-mcp',
    tags: ['Remote', 'Local'],
    context: 'W / A / C', ease: 'Medium', priority: 'High',
  }, { platforms: ['both'], roles: ['dev', 'sales-marketing', 'copy'] }),
  withMeta({
    title: 'QuickBooks Online MCP',
    desc: 'Local stdio, OAuth 2.0, 29 entity types, 11 financial reports. Write/update/delete suppression built-in.',
    url: 'https://github.com/intuit/quickbooks-online-mcp-server',
    tags: ['Local', 'Finance'],
    context: 'C', ease: 'High', priority: 'Medium',
  }, { platforms: ['claude'], roles: ['executive', 'dev'] }),
  withMeta({
    title: 'Google Analytics MCP',
    desc: 'Official, experimental, read-only. Natural-language KPI analysis, event trends, marketing planning.',
    url: 'https://developers.google.com/analytics/devguides/MCP',
    tags: ['Local', 'Read-only'],
    context: 'C', ease: 'Medium', priority: 'Medium',
  }, { platforms: ['claude'], roles: ['sales-marketing', 'executive', 'dev'] }),
  withMeta({
    title: 'Zoom MCP',
    desc: 'Official MCP docs. Meeting, transcript, scheduling, support handoff. Good second-wave integration.',
    url: 'https://developers.zoom.us/docs/mcp/',
    tags: ['Remote'],
    context: 'W / C', ease: 'Medium', priority: 'Medium',
  }, { platforms: ['both'], roles: ['sales-marketing', 'executive'] }),
  withMeta({
    title: 'AWS MCP Collection',
    desc: 'AWS Labs open-source MCP collection. Expose service-specific slices only — billing, CloudWatch, deployments.',
    url: 'https://github.com/awslabs/mcp',
    tags: ['Local', 'Open-source'],
    context: 'C', ease: 'Medium–High', priority: 'Medium',
  }, { platforms: ['claude'], roles: ['dev'] }),
  withMeta({
    title: 'Seamless.AI MCP',
    desc: 'Native MCP server announced. Prospect lookup and enrichment. Keep in pilot queue — not first tranche.',
    url: 'https://seamless.ai/customers/blog/products/seamless-mcp-server',
    tags: ['Remote'],
    context: 'W / C', ease: 'Medium', priority: 'Low–Medium',
  }, { platforms: ['both'], roles: ['sales-marketing'] }),
  withMeta({
    title: 'Breadwinner',
    desc: 'Positioned as ChatGPT finance integration for NetSuite, QuickBooks, Xero. Validate API/auth before committing.',
    url: 'https://breadwinner.com/interfaces/chatgpt/',
    tags: ['Finance'],
    context: 'W', ease: 'Medium', priority: 'Low–Medium',
  }, { platforms: ['chatgpt'], roles: ['executive', 'sales-marketing'] }),
  withMeta({
    title: 'Salesloft MCP',
    desc: 'Help-center article exists. Validate capabilities before engineering commits — docs not fully confirmed.',
    url: 'https://help.salesloft.com/s/article/Salesloft-MCP-Server?language=en_US',
    tags: ['Remote'],
    context: 'W / C', ease: 'Medium', priority: 'Low–Medium',
  }, { platforms: ['both'], roles: ['sales-marketing', 'executive'] }),
];

/* ── Agents ───────────────────────────────────────────────────── */
const AGENTS = [
  withMeta({
    title: 'Baitty', role: 'Project Management',
    desc: 'Monitors all open opportunities, routes work to sub-agents, sends deadline reminders.',
    tags: ['Strategy', 'Orchestrator'],
    tasks: [
      'Every 5 min: pull open opportunities grouped by stage',
      'Determine if action is needed on any opportunity',
      'If unassigned tasks found, create sub-agent to handle',
      'If Opportunity Approval found, ask Bryain to review',
      'If Pre-Dev Approval found, ask Devain to start',
      'If assigned task is near completion date, send Slack status request',
      'If task is overdue, send kind but urgent reminder',
      'If opportunity trending toward overdue, warn opportunity and account owners',
    ],
  }, { platforms: ['both'], roles: ['dev', 'executive'] }),
  withMeta({
    title: 'Bryain', role: 'Opportunity Approval',
    desc: 'Validates CSD completeness and account fields. Reviews strategy before work begins.',
    tags: ['Retention', 'Review'],
    tasks: [
      'When directed, pull opportunities in Opportunity Approval stage',
      'Check all account and opportunity fields are correctly filled',
      'Get list of CSDs',
      'Confirm all necessary information is provided in CSD',
      'Note areas where requirements are unclear',
      'Add report to the task comments field',
      'Review strategy',
    ],
  }, { platforms: ['both'], roles: ['executive'] }),
  withMeta({
    title: 'Aimanda', role: 'Copy Writer',
    desc: 'Generates onsite and email copy per CSD. Checks spam signals and optimizes for campaign goals.',
    tags: ['Creative'],
    tasks: [
      'When directed, generate copy for each CSD',
      'Review onsite design copy',
      'Create email copy for each attempt',
      'Check for spammy words',
      'Optimize copy to achieve the campaign\'s strategic goals',
    ],
  }, { platforms: ['both'], roles: ['copy', 'executive'] }),
  withMeta({
    title: 'Raimond', role: 'Design',
    desc: 'Creates Figma deliverables from CSD briefs. Matches brand guide and website capture.',
    tags: ['Design', 'Code'],
    tasks: [
      'When instructed by Baitty, begin Figma creation with CSD brief',
      'Generate a Figma file using Upsellify template',
      'Pull brand guide and Figma Capture from the client website',
      'Use Figma Agent to review design details and match brand guide',
      'Add link to CSD Mockup or Deliverables field',
      'Add note to task comment when complete',
    ],
  }, { platforms: ['claude'], roles: ['copy', 'dev'] }),
  withMeta({
    title: 'Devain', role: 'Development',
    desc: 'Implements campaigns from Pre-Dev Approval tasks. Builds, compares to mockup, commits to git.',
    tags: ['Engineering'],
    tasks: [
      'When instructed, begin review of Unassigned Pre Dev Approval tasks',
      'Click Create Campaign in each CSD',
      'Compare generated campaign data to CSD, update where needed',
      'Create company_json for all generated campaigns',
      'Per campaign: review company_json, site settings, decision tables, configs',
      'Take screenshot at same screen size as the mockup',
      'Compare visual diff between mockup and code; adjust HTML/CSS to match',
      'Confirm JavaScript functionality',
      'Generate usi_app and usi_pixel code',
      'Commit code to git',
      'Add completion notes to Pre Dev Approval task comments',
    ],
  }, { platforms: ['claude'], roles: ['dev'] }),
  // {
  //   title: 'Michail', role: 'Code Review',
  //   desc: 'Validates campaign config and code. Runs browser tests, checks console and network activity.',
  //   tags: ['Review'],
  //   tasks: [
  //     'Every 10 min: check for unassigned Code Review tasks',
  //     'Get current opportunity and CSD information',
  //     'Check git changes during the opportunity timeline',
  //     'Verify each campaign matches CSD configuration',
  //     'Check company_json, app file, and pixel',
  //     'Confirm testing instructions cover main test path for all CSD requirements',
  //     'Open browser and perform testing instructions',
  //     'Check console and network tab for Upsellit activity',
  //     'Verify campaign loads, CTA clickable, destination correct',
  //     'Add notes to Code Review task comments field',
  //   ],
  // },
  // {
  //   title: 'Kairi', role: 'QA',
  //   desc: 'Full test coverage on QA Campaign and QA Live Site tasks. Checks live performance data.',
  //   tags: ['Knowledge', 'Review'],
  //   tasks: [
  //     'Every 10 min: check for unassigned QA Campaign and QA Live Site tasks',
  //     'Get current opportunity and CSD information',
  //     'Verify each campaign is correctly configured and matches CSDs',
  //     'Confirm testing instructions cover all test paths for all CSD requirements',
  //     'Open browser and perform testing instructions',
  //     'Verify campaign loads, CTA clickable, destination correct',
  //     'If Live Site review, check for performance data',
  //     'Add notes to task comments field',
  //   ],
  // },
  // {
  //   title: 'Aishley', role: 'CSM',
  //   desc: 'Client success monitoring. Audits KPIs, monitors emails and site alerts, suggests campaign proposals.',
  //   tags: ['Productivity'],
  //   tasks: [
  //     'Perform client performance audit',
  //     'Confirm all live sites have loads, displays, clicks, and sales',
  //     'Check KPI trends WoW, MoM, YoY',
  //     'Suggest Upsellit campaigns to propose to clients',
  //     'Monitor emails to CSM support groups — read, summarize, draft responses',
  //     'Monitor Live and Dead site alerts — verify if expected',
  //     'Monitor other email reports',
  //   ],
  // },
  // {
  //   title: 'Ainna', role: 'Data Analysis',
  //   desc: 'Revenue projections, affiliate monitoring, campaign performance analysis, email deliverability research.',
  //   tags: ['Analytics'],
  //   tasks: [
  //     'Project revenue for new clients',
  //     'Review projected vs actual revenue per client',
  //     'Identify underperforming campaigns',
  //     'Check for affiliate discrepancies',
  //     'Monitor for changes in affiliate terms',
  //     'Identify commonalities between campaigns with unexpected results',
  //     'Identify outliers',
  //     'Get average load/launch/click/sales rates for similar clients and campaigns',
  //     'Identify patterns in low email send rates',
  //     'Check if spam words correlate with lower send rates',
  //     'Check if text-to-image ratio correlates with send rates',
  //   ],
  // },
  // {
  //   title: 'Braiks', role: 'Process Review',
  //   desc: 'Performs Manager Review tasks, analyzes opportunity timelines, suggests process improvements.',
  //   tags: ['Research', 'Review'],
  //   tasks: [
  //     'Monitor for Manager Review tasks; perform Code Review and QA',
  //     'When opportunity is complete: check on-time rates, total time, time in progress vs waiting',
  //     'Review agent contributions',
  //     'Check for excessive Fix and QA back-and-forth',
  //     'Identify opportunities for improvement',
  //     'Suggest edits to agent instructions for future situations',
  //     'Suggest edits to QA test cases',
  //     'Find where generated code was modified; check if Figma plugin can be updated to match',
  //     'Find admin edits; check where Create Campaign can be adjusted',
  //   ],
  // },
  withMeta({
    title: 'ChrisGPT', role: 'Executive',
    desc: 'Company-wide orchestrator. Daily executive briefs, escalations, bottleneck detection, weekly leadership summaries.',
    tags: ['Orchestrator'],
    tasks: [
      'Daily: pull executive summary of all active opportunities, tasks, escalations, and agent activity',
      'Identify company-wide blockers across all stages',
      'Review overdue, near-due, and stalled work',
      'Prioritize issues by revenue impact, client risk, and launch urgency',
      'Monitor unassigned work and confirm Baitty has assigned correctly',
      'Summarize at-risk accounts and reasons',
      'Send Slack summaries when executive attention is needed',
      'Generate daily brief: revenue risk, launch risk, QA risk, escalations, bottlenecks, decisions needed',
      'Escalate urgent issues to Brooks or department lead',
      'Maintain executive timeline for each opportunity',
      'Generate weekly leadership summary: completions, delays, avg time, blockers, revenue at risk',
      'Recommend process changes based on recurring issues',
      'Detect when agents are stuck, looping, or producing low-confidence output',
      'Pause or escalate agent activity for high-risk or data-inconsistency scenarios',
    ],
  }, { platforms: ['both'], roles: ['executive'] })
];

/* ── Shared skills ────────────────────────────────────────────── */
const SHARED_SKILLS = [
  withMeta({ title: 'Cronjob runner', desc: 'Periodically runs a script on schedule.' }, { platforms: ['claude'], roles: ['dev', 'executive'] }),
  withMeta({ title: 'SOQL query', desc: 'Run SOQL query to identify a subset of SF tasks.' }, { platforms: ['both'], roles: ['dev', 'sales-marketing', 'executive'] }),
  withMeta({ title: 'SF Account summary', desc: 'Get Salesforce Account summary.' }, { platforms: ['both'], roles: ['sales-marketing', 'executive'] }),
  withMeta({ title: 'SF Opportunity summary', desc: 'Get Salesforce Opportunity summary.' }, { platforms: ['both'], roles: ['sales-marketing', 'executive', 'dev'] }),
  withMeta({ title: 'Get all CSD data', desc: 'Retrieve full CSD record and related fields.' }, { platforms: ['both'], roles: ['dev', 'copy', 'executive'] }),
  withMeta({ title: 'Get Admin data', desc: 'Read admin configuration data.' }, { platforms: ['claude'], roles: ['dev'] }),
  withMeta({ title: 'Get Client Reporting data', desc: 'Pull client performance reporting data.' }, { platforms: ['both'], roles: ['sales-marketing', 'executive'] }),
  withMeta({ title: 'Browser campaign test', desc: 'Use browser to test campaigns.' }, { platforms: ['claude'], roles: ['dev'] }),
  withMeta({ title: 'Update SF Task comment', desc: 'Write notes to a Salesforce Task comment field.' }, { platforms: ['both'], roles: ['dev', 'copy', 'executive'] }),
  withMeta({ title: 'Read app/pixel/git', desc: 'Read app file, pixel file, and git history.' }, { platforms: ['claude'], roles: ['dev'] }),
  withMeta({ title: 'Visual diff', desc: 'Create visual diff of mockup and campaign.' }, { platforms: ['claude'], roles: ['dev', 'copy'] }),
  withMeta({ title: 'Email / Slack messaging', desc: 'Read and send email and Slack messages.' }, { platforms: ['both'], roles: ['sales-marketing', 'copy', 'executive'] }),
];

/* ── Skills framework ─────────────────────────────────────────── */
const SKILL_FRAMEWORK = [
  withMeta({ title: 'merchant-diagnosis', desc: 'CRM, analytics, and performance data ingestion.' }, { platforms: ['both'], roles: ['sales-marketing', 'executive', 'dev'] }),
  withMeta({ title: 'upsell-strategy', desc: 'Historical test retrieval and competitive context lookup.' }, { platforms: ['both'], roles: ['sales-marketing', 'copy', 'executive'] }),
  withMeta({ title: 'creative-variant-builder', desc: 'Brand asset retrieval and design system access.' }, { platforms: ['both'], roles: ['copy', 'dev'] }),
  withMeta({ title: 'design-to-component', desc: 'Figma frames to production code pipeline.' }, { platforms: ['claude'], roles: ['dev'] }),
  withMeta({ title: 'repo-triage', desc: 'Issue, PR, and log analysis across GitHub / YouTrack.' }, { platforms: ['claude'], roles: ['dev'] }),
  withMeta({ title: 'knowledge-brief', desc: 'Grounded documentation synthesis from Dropbox / Salesforce.' }, { platforms: ['both'], roles: ['copy', 'sales-marketing', 'executive'] }),
  withMeta({ title: 'commercial-analytics', desc: 'Revenue benchmarking via GA + QuickBooks.' }, { platforms: ['both'], roles: ['executive', 'sales-marketing'] }),
  withMeta({ title: 'market-intel-brief', desc: 'Competitive intelligence from web research and Dropbox.' }, { platforms: ['both'], roles: ['sales-marketing', 'executive', 'copy'] }),
];

/* ── Installation playbooks ───────────────────────────────────── */
const PLAYBOOKS = [
  withMeta({
    title: 'Dropbox',
    desc: 'Remote OAuth SaaS — documented across all surfaces. Fastest reference implementation.',
    steps: [
      { surface: 'Claude Code', code: 'claude mcp add --transport http DropboxMCP https://mcp.dropbox.com/mcp' },
      { surface: 'Codex CLI', code: 'codex mcp add DropboxMCP --url https://mcp.dropbox.com/mcp\ncodex mcp login DropboxMCP' },
      { surface: 'Claude Web / Desktop', prose: 'Settings → Connectors → Add custom connector → Name: "Dropbox MCP" → URL: https://mcp.dropbox.com/mcp → Add.' },
      { surface: 'ChatGPT Web', prose: 'Settings → Apps & Connectors → Advanced settings → Developer mode ON → Settings → Apps → Create app → Name: "Dropbox MCP" → URL: https://mcp.dropbox.com/mcp → Auth: OAuth → Create.' },
    ],
  }, { platforms: ['both'], roles: ['dev', 'sales-marketing', 'copy', 'executive'] }),
  withMeta({
    title: 'Shopify',
    desc: 'Two-product pattern: Storefront MCP for buyer-agents; AI Toolkit/Dev MCP for operators.',
    steps: [
      { surface: 'Claude Code — Dev MCP', code: 'claude mcp add --transport stdio shopify-dev-mcp -- npx -y @shopify/dev-mcp@latest' },
      { surface: 'Claude Code — AI Toolkit plugin', code: 'claude plugin install shopify-ai-toolkit@claude-plugins-official' },
      { surface: 'Codex CLI — Dev MCP', code: '[mcp_servers.shopify-dev-mcp]\ncommand = "npx"\nargs = ["-y", "@shopify/dev-mcp@latest"]' },
      { surface: 'Codex Desktop', prose: 'Open Codex desktop → Plugins marketplace → Search "Shopify" → Add Plugin on "Shopify AI Toolkit".' },
    ],
  }, { platforms: ['both'], roles: ['dev', 'sales-marketing', 'copy'] }),
  withMeta({
    title: 'QuickBooks Online',
    desc: 'Local-first stdio server — wrap behind UpsellyMCP for web deployment. Disable writes by default.',
    steps: [
      { surface: 'Install & Build', code: 'git clone https://github.com/intuit/quickbooks-online-mcp-server.git\ncd quickbooks-online-mcp-server\nnpm install && npm run build' },
      { surface: '.env Config', code: 'QUICKBOOKS_CLIENT_ID=your_client_id\nQUICKBOOKS_CLIENT_SECRET=your_client_secret\nQUICKBOOKS_ENVIRONMENT=sandbox\nQUICKBOOKS_REFRESH_TOKEN=your_refresh_token\nQUICKBOOKS_REALM_ID=your_realm_id\n# QUICKBOOKS_DISABLE_WRITE=true\n# QUICKBOOKS_DISABLE_UPDATE=true\n# QUICKBOOKS_DISABLE_DELETE=true' },
      {
        surface: 'Claude Code config', code: `{
  "mcpServers": {
    "quickbooks": {
      "command": "node",
      "args": ["path/to/dist/index.js"],
      "env": {
        "QUICKBOOKS_CLIENT_ID": "...",
        "QUICKBOOKS_REFRESH_TOKEN": "...",
        "QUICKBOOKS_REALM_ID": "...",
        "QUICKBOOKS_DISABLE_WRITE": "false"
      }
    }
  }
}`,
      },
    ],
  }, { platforms: ['claude'], roles: ['dev', 'executive'] }),
];

/* ── Deployment notes ─────────────────────────────────────────── */
const DEPLOYMENT = [
  withMeta({
    title: 'Recommended Transport & Auth',
    desc: 'Transport: Remote Streamable HTTP for business tools. Auth: OAuth 2.0 with refresh tokens. Keep actual services private; publish read-only search/fetch tools first; add write scopes with human approval.',
    tags: ['Architecture'],
  }, { platforms: ['both'], roles: ['dev', 'sales-marketing', 'copy', 'executive'] }),
  withMeta({
    title: 'Security: Prompt Injection',
    desc: 'Claude Code warns directly about prompt-injection risk from MCP servers. Prefer official vendor servers, pin internal manifests, keep tool descriptions minimal, run a server allowlist rather than a marketplace free-for-all.',
    tags: ['Security'],
  }, { platforms: ['both'], roles: ['dev', 'executive'] }),
  withMeta({
    title: 'Security: Data Exposure',
    desc: 'OpenAI warns custom MCP servers may receive sensitive user data. Approval by default for writes and any connector that can exfiltrate customer data or internal IP. OAuth with Client ID Metadata Documents preferred.',
    tags: ['Security', 'Compliance'],
  }, { platforms: ['both'], roles: ['dev', 'executive', 'sales-marketing'] }),
  withMeta({
    title: 'Token Budget — Build Phase',
    desc: 'Codex-assisted implementation: 4M–10M tokens. Repo planning: 500k–1.5M. End-to-end testing: 1M–3M. Total: 5M–15M tokens.',
    tags: ['Costs'],
  }, { platforms: ['both'], roles: ['dev', 'executive'] }),
  withMeta({
    title: 'Token Budget — Runtime (Phase 1)',
    desc: 'Conservative: 10M–25M/mo. Normal production: 30M–75M/mo. Heavy QA: 100M–200M/mo. (10 active opportunities/week baseline)',
    tags: ['Costs', 'Runtime'],
  }, { platforms: ['both'], roles: ['executive'] }),
  withMeta({
    title: 'Integration Buckets',
    desc: 'Remote SaaS MCPs (Dropbox, YouTrack) = easiest. Local stdio behind broker (Salesforce DX, QuickBooks, GA, Shopify Dev) = trusted operators only. Plugin-first (Codex, Shopify AI Toolkit) = engineer-facing.',
    tags: ['Architecture'],
  }, { platforms: ['both'], roles: ['dev', 'executive', 'sales-marketing'] }),
  withMeta({
    title: 'First-Wave Priorities',
    desc: '1. Baitty + Ainna (read-mostly, high-value). 2. Kairi (internal knowledge, ChatGPT-friendly). 3. Raimond + Devain (design-to-code loop). 4. ChrisGPT (thin orchestrator).',
    tags: ['Roadmap'],
  }, { platforms: ['both'], roles: ['executive', 'dev'] }),
];

/* ── Tag colors ───────────────────────────────────────────────── */
const TAG_COLORS = {
  Claude: '#3c8cda', API: '#3c8cda', CLI: '#3c8cda', MCP: '#10b981', Spec: '#10b981',
  OpenAI: '#9ca3af', ChatGPT: '#9ca3af', Remote: '#10b981', OAuth: '#a855f7',
  Local: '#9ca3af', Bearer: '#f59e0b', Beta: '#f0198c', 'Read-only': '#9ca3af',
  'Open-source': '#9ca3af', Finance: '#f59e0b', Analytics: '#f59e0b', Creative: '#a855f7',
  Engineering: '#3c8cda', Design: '#f0198c', Code: '#f0198c',
  Strategy: '#10b981', Revenue: '#10b981', Retention: '#f0198c', Review: '#9ca3af',
  Knowledge: '#a855f7', Productivity: '#3c8cda', Research: '#f59e0b',
  Orchestrator: '#9ca3af', Architecture: '#3c8cda', Security: '#f0198c',
  Compliance: '#f0198c', Costs: '#f59e0b', Runtime: '#f59e0b', Roadmap: '#10b981',
};

/* ── Components ───────────────────────────────────────────────── */
function Tag({ label }) {
  const color = TAG_COLORS[label] || '#6b7280';
  return (
    <span style={{
      fontSize: 10, fontWeight: 600, letterSpacing: '0.04em',
      padding: '2px 6px', borderRadius: 4,
      background: color + '22', color,
    }}>
      {label}
    </span>
  );
}

function ResourceCard({ resource, accentColor }) {
  return (
    <div style={{
      background: 'var(--bg2)', border: '1px solid var(--border)',
      borderRadius: 8, padding: '12px 14px',
      display: 'flex', flexDirection: 'column', gap: 6,
    }}>
      <div style={{ fontWeight: 600, fontSize: 13, lineHeight: 1.3 }}>
        {resource.url ? (
          <a href={resource.url} target="_blank" rel="noreferrer"
            style={{ color: accentColor, textDecoration: 'none' }}
            onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
            onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
          >
            {resource.title}<ExternalIcon />
          </a>
        ) : (
          <span style={{ color: 'var(--text)' }}>{resource.title}</span>
        )}
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-sec)', lineHeight: 1.5 }}>{resource.desc}</div>
      {resource.note && (
        <code style={{
          fontSize: 10, background: 'var(--bg3, rgba(0,0,0,0.3))', color: 'var(--text-sec)',
          padding: '4px 8px', borderRadius: 4, display: 'block', wordBreak: 'break-all',
        }}>{resource.note}</code>
      )}
      {resource.tags?.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 2 }}>
          {resource.tags.map(t => <Tag key={t} label={t} />)}
        </div>
      )}
    </div>
  );
}

function McpCard({ resource, accentColor }) {
  const priorityColor = resource.priority === 'High' ? '#10b981' : resource.priority === 'Medium' ? '#f59e0b' : '#9ca3af';
  return (
    <div style={{
      background: 'var(--bg2)', border: '1px solid var(--border)',
      borderRadius: 8, padding: '12px 14px',
      display: 'flex', flexDirection: 'column', gap: 6,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ fontWeight: 600, fontSize: 13, lineHeight: 1.3 }}>
          <a href={resource.url} target="_blank" rel="noreferrer"
            style={{ color: accentColor, textDecoration: 'none' }}
            onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
            onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
          >
            {resource.title}<ExternalIcon />
          </a>
        </div>
        <span style={{
          fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10, flexShrink: 0,
          background: priorityColor + '22', color: priorityColor,
        }}>{resource.priority}</span>
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-sec)', lineHeight: 1.5 }}>{resource.desc}</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 10, color: 'var(--text-sec)', background: 'var(--bg3, rgba(0,0,0,0.2))', padding: '2px 7px', borderRadius: 4 }}>
          {resource.context}
        </span>
        <span style={{ fontSize: 10, color: 'var(--text-sec)' }}>Ease: {resource.ease}</span>
        {resource.tags?.map(t => <Tag key={t} label={t} />)}
      </div>
      {resource.note && (
        <code style={{
          fontSize: 10, background: 'var(--bg3, rgba(0,0,0,0.3))', color: 'var(--text-sec)',
          padding: '4px 8px', borderRadius: 4, display: 'block', wordBreak: 'break-all',
        }}>{resource.note}</code>
      )}
    </div>
  );
}

function AgentCard({ agent, accentColor }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div style={{
      background: 'var(--bg2)', border: '1px solid var(--border)',
      borderRadius: 8, padding: '12px 14px',
      display: 'flex', flexDirection: 'column', gap: 6,
    }}>
      <div
        onClick={() => setExpanded(e => !e)}
        style={{ cursor: 'pointer', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}
      >
        <div>
          <div style={{ fontWeight: 600, fontSize: 13, color: accentColor }}>{agent.title}</div>
          <div style={{ fontSize: 10, color: 'var(--text-sec)', marginTop: 1 }}>{agent.role}</div>
        </div>
        <ChevronDown open={expanded} />
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-sec)', lineHeight: 1.5 }}>{agent.desc}</div>
      {expanded && (
        <ul style={{ margin: '4px 0 0', padding: '0 0 0 16px', display: 'flex', flexDirection: 'column', gap: 3 }}>
          {agent.tasks.map((t, i) => (
            <li key={i} style={{ fontSize: 11, color: 'var(--text-sec)', lineHeight: 1.5 }}>{t}</li>
          ))}
        </ul>
      )}
      {agent.tags?.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 2 }}>
          {agent.tags.map(t => <Tag key={t} label={t} />)}
        </div>
      )}
    </div>
  );
}

function PlaybookCard({ playbook }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div style={{
      background: 'var(--bg2)', border: '1px solid var(--border)',
      borderRadius: 8, overflow: 'hidden',
    }}>
      <div
        onClick={() => setExpanded(e => !e)}
        style={{
          padding: '12px 14px', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
        }}
      >
        <div>
          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>{playbook.title}</div>
          <div style={{ fontSize: 12, color: 'var(--text-sec)', marginTop: 3 }}>{playbook.desc}</div>
        </div>
        <ChevronDown open={expanded} />
      </div>
      {expanded && (
        <div style={{ borderTop: '1px solid var(--border)', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {playbook.steps.map((step, i) => (
            <div key={i}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-sec)', marginBottom: 4 }}>{step.surface}</div>
              {step.code ? (
                <pre style={{
                  margin: 0, fontSize: 11, lineHeight: 1.6,
                  background: 'var(--bg3, rgba(0,0,0,0.3))', color: 'var(--text-sec)',
                  padding: '8px 10px', borderRadius: 6, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                }}>{step.code}</pre>
              ) : (
                <div style={{ fontSize: 12, color: 'var(--text-sec)', lineHeight: 1.5 }}>{step.prose}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ReplacementCard({ item }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div style={{
      background: 'var(--bg2)', border: '1px solid var(--border)',
      borderRadius: 8, overflow: 'hidden',
    }}>
      <div
        onClick={() => setExpanded(e => !e)}
        style={{
          padding: '12px 14px', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
        }}
      >
        <div>
          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>{item.title}</div>
          <div style={{ fontSize: 11, color: '#10b981', marginTop: 3, fontFamily: 'monospace' }}>{item.agent}</div>
        </div>
        <ChevronDown open={expanded} />
      </div>
      {expanded && (
        <div style={{ borderTop: '1px solid var(--border)', padding: '12px 14px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#10b981', marginBottom: 6 }}>Replaceable with MCPs</div>
            <ul style={{ margin: 0, padding: '0 0 0 14px', display: 'flex', flexDirection: 'column', gap: 3 }}>
              {item.replaceable.map((r, i) => (
                <li key={i} style={{ fontSize: 11, color: 'var(--text-sec)', lineHeight: 1.5 }}>{r}</li>
              ))}
            </ul>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#f0198c', marginBottom: 6 }}>Harder to replace</div>
            <ul style={{ margin: 0, padding: '0 0 0 14px', display: 'flex', flexDirection: 'column', gap: 3 }}>
              {item.notReplaceable.map((r, i) => (
                <li key={i} style={{ fontSize: 11, color: 'var(--text-sec)', lineHeight: 1.5 }}>{r}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function AudienceSelector({ label, options, value, onChange }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-sec)' }}>
        {label}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {options.map(option => {
          const active = option.id === value;
          return (
            <button
              key={option.id}
              onClick={() => onChange(option.id)}
              style={{
                fontSize: 12,
                fontWeight: 600,
                padding: '7px 12px',
                borderRadius: 999,
                border: '1px solid var(--border)',
                cursor: 'pointer',
                background: active ? 'rgba(60,140,218,0.14)' : 'var(--bg2)',
                color: active ? '#3c8cda' : 'var(--text-sec)',
              }}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function AudienceBrief({ guide, roleLabel, platformLabel, recommendedSections }) {
  return (
    <div style={{
      background: 'var(--bg2)',
      border: '1px solid var(--border)',
      borderRadius: 10,
      padding: '16px 18px',
      display: 'grid',
      gridTemplateColumns: '1.3fr 1fr',
      gap: 18,
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#3c8cda' }}>
          {roleLabel} · {platformLabel}
        </div>
        <div style={{ fontSize: 14, color: 'var(--text)', lineHeight: 1.6 }}>{guide.summary}</div>
        <div style={{ fontSize: 12, color: 'var(--text-sec)', lineHeight: 1.6 }}>{guide.emphasis}</div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-sec)', marginBottom: 6, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            Focus Areas
          </div>
          <ul style={{ margin: 0, padding: '0 0 0 16px', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {guide.focus.map((item) => (
              <li key={item} style={{ fontSize: 12, color: 'var(--text-sec)', lineHeight: 1.5 }}>{item}</li>
            ))}
          </ul>
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-sec)', marginBottom: 6, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            Recommended Sections
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {recommendedSections.map((section) => <Tag key={section.id} label={section.label} />)}
          </div>
        </div>
      </div>
    </div>
  );
}

function SurfaceTable({ rows }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr>
            {['Surface', 'Best Use', 'How Added', 'Write Actions', 'Key Constraints'].map(h => (
              <th key={h} style={{
                textAlign: 'left', padding: '8px 12px', fontSize: 11, fontWeight: 700,
                color: 'var(--text-sec)', borderBottom: '1px solid var(--border)',
                whiteSpace: 'nowrap',
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((s, i) => (
            <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
              <td style={{ padding: '10px 12px', fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap' }}>{s.name}</td>
              <td style={{ padding: '10px 12px', color: 'var(--text-sec)', lineHeight: 1.5 }}>{s.bestUse}</td>
              <td style={{ padding: '10px 12px', color: 'var(--text-sec)', fontFamily: 'monospace', fontSize: 11 }}>{s.howAdded}</td>
              <td style={{ padding: '10px 12px', color: 'var(--text-sec)' }}>{s.writes}</td>
              <td style={{ padding: '10px 12px', color: 'var(--text-sec)' }}>{s.constraints}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── Section definitions ──────────────────────────────────────── */
const SECTIONS = [
  { id: 'platforms',     label: 'AI Platforms',           color: '#3c8cda', type: 'cards',       data: PLATFORMS },
  { id: 'surface',       label: 'Surface Comparison',     color: '#3c8cda', type: 'table',       data: SURFACES },
  { id: 'mcp',           label: 'MCP Integrations',       color: '#10b981', type: 'mcp',         data: MCP_INTEGRATIONS },
  { id: 'agents',        label: 'Agent Roster',           color: '#a855f7', type: 'agents',      data: AGENTS },
  { id: 'shared-skills', label: 'Shared Skills',          color: '#a855f7', type: 'cards',       data: SHARED_SKILLS },
  { id: 'skills',        label: 'Skills Framework',       color: '#f0198c', type: 'cards',       data: SKILL_FRAMEWORK },
  { id: 'playbooks',     label: 'Installation Playbooks', color: '#f59e0b', type: 'playbooks',   data: PLAYBOOKS },
  //{ id: 'replacement',   label: 'Replacement Analysis',   color: '#10b981', type: 'replacement', data: REPLACEMENTS },
  { id: 'deployment',    label: 'Deployment & Security',  color: '#f59e0b', type: 'cards',       data: DEPLOYMENT },
];

/* ── Main view ────────────────────────────────────────────────── */
export default function Learn() {
  const [platformFilter, setPlatformFilter] = useState('both');
  const [roleFilter, setRoleFilter] = useState('all');
  const [activeSection, setActiveSection] = useState(null);

  const guide = PROFILE_GUIDES[roleFilter]?.[platformFilter] || null;
  const recommendedSectionIds = new Set(guide?.sections || []);
  const recommendedSections = SECTIONS.filter(section => recommendedSectionIds.has(section.id));
  const platformLabel = PLATFORM_OPTIONS.find(option => option.id === platformFilter)?.label || 'Both';
  const roleLabel = ROLE_OPTIONS.find(option => option.id === roleFilter)?.label || 'Dev';

  const filteredSections = SECTIONS
    .map(section => {
      const data = filterItems(section.data, platformFilter, roleFilter);
      return { ...section, data };
    })
    .filter(section => {
      if (section.type === 'table') return section.data.length > 0;
      return !section.data || section.data.length > 0;
    });

  const visibleSections = (activeSection
    ? filteredSections.filter(s => s.id === activeSection)
    : filteredSections
  ).sort((a, b) => {
    const aRecommended = recommendedSectionIds.has(a.id) ? 0 : 1;
    const bRecommended = recommendedSectionIds.has(b.id) ? 0 : 1;
    return aRecommended - bRecommended;
  });

  function renderSection(section) {
    if (section.type === 'table') {
      return <SurfaceTable rows={section.data} />;
    }
    if (section.type === 'playbooks') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {section.data.map(p => <PlaybookCard key={p.title} playbook={p} />)}
        </div>
      );
    }
    if (section.type === 'replacement') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {section.data.map(r => <ReplacementCard key={r.title} item={r} />)}
        </div>
      );
    }
    if (section.type === 'agents') {
      return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
          {section.data.map(a => <AgentCard key={a.title} agent={a} accentColor={section.color} />)}
        </div>
      );
    }
    if (section.type === 'mcp') {
      return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
          {section.data.map(r => <McpCard key={r.title} resource={r} accentColor={section.color} />)}
        </div>
      );
    }
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10 }}>
        {section.data.map(r => <ResourceCard key={r.title} resource={r} accentColor={section.color} />)}
      </div>
    );
  }

  return (
    <div style={{ padding: '28px 32px', maxWidth: 1020, margin: '0 auto' }}>

      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', margin: 0, marginBottom: 6 }}>Learn</h1>
        <p style={{ fontSize: 13, color: 'var(--text-sec)', margin: 0 }}>
          Audience-targeted MCP setup guides, surface comparison, agent patterns, installation playbooks, and replacement analysis.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 18 }}>
        <AudienceSelector label="Client Surface" options={PLATFORM_OPTIONS} value={platformFilter} onChange={setPlatformFilter} />
        <AudienceSelector label="Audience" options={ROLE_OPTIONS} value={roleFilter} onChange={setRoleFilter} />
      </div>

      {guide && (
        <div style={{ marginBottom: 28 }}>
          <AudienceBrief
            guide={guide}
            roleLabel={roleLabel}
            platformLabel={platformLabel}
            recommendedSections={recommendedSections}
          />
        </div>
      )}

      {/* Filter pills */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 28 }}>
        <button
          onClick={() => setActiveSection(null)}
          style={{
            fontSize: 12, fontWeight: 500, padding: '5px 12px', borderRadius: 20,
            border: '1px solid var(--border)', cursor: 'pointer',
            background: activeSection === null ? '#3c8cda' : 'var(--bg2)',
            color: activeSection === null ? '#fff' : 'var(--text-sec)',
          }}
        >All</button>
        {filteredSections.map(s => (
          <button key={s.id}
            onClick={() => setActiveSection(s.id === activeSection ? null : s.id)}
            style={{
              fontSize: 12, fontWeight: 500, padding: '5px 12px', borderRadius: 20,
              border: '1px solid var(--border)', cursor: 'pointer',
              background: activeSection === s.id ? s.color + '22' : 'var(--bg2)',
              color: activeSection === s.id ? s.color : 'var(--text-sec)',
            }}
          >{s.label}</button>
        ))}
      </div>

      {/* Sections */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 36 }}>
        {visibleSections.map(section => (
          <div key={section.id}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <div style={{ width: 3, height: 18, borderRadius: 2, background: section.color, flexShrink: 0 }} />
              <h2 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', margin: 0, letterSpacing: '0.02em' }}>
                {section.label}
              </h2>
              <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
              {section.data && <span style={{ fontSize: 11, color: 'var(--text-sec)' }}>{section.data.length}</span>}
            </div>
            {renderSection(section)}
          </div>
        ))}
      </div>

      {/* Footer */}
      {/* <div style={{
        marginTop: 48, padding: '14px 16px', border: '1px solid var(--border)',
        borderRadius: 8, background: 'var(--bg2)', fontSize: 12, color: 'var(--text-sec)',
      }}>
        Source: <a
          href="https://docs.google.com/document/d/11UAskfAtk4RdiD9UBPp1FmM1hkEnhCZ0sgTCgOFO0Ns/edit"
          target="_blank" rel="noreferrer"
          style={{ color: '#3c8cda', textDecoration: 'none' }}
        >MCP Setup and Integration Guide</a>
        {' '}— internal Google Doc with installation playbooks, surface comparisons, security guidance, and open questions.
        {' '}Context key: W = web, A = app surface, C = CLI/terminal.
      </div> */}

    </div>
  );
}
